import { createSignal, createEffect, on, onMount, onCleanup, For, Show } from "solid-js";
import { createStore } from "solid-js/store";
import { View, Text, hexColor } from "@lightningtv/solid";
import OnScreenKeyboard, { KeyboardMode, keyboardWidth } from "./OnScreenKeyboard";
import Box from "../Box";
import type { RegistrationPlan, RegistrationRequest } from "../../utils/registration";

type FieldId = "name" | "phone" | "city" | "address" | "postalCode" | "nif";

type FieldDef = {
  label: string;
  hint: string;
  keyboard: KeyboardMode;
  maxLength: number;
  // Characters this field accepts. Others are ignored, e.g. "+" on the NIF keypad.
  allowed?: RegExp;
  validate?: (value: string) => string | null;
};

const FIELDS: Record<FieldId, FieldDef> = {
  name: { label: "Full name", hint: "Your name", keyboard: "text", maxLength: 60 },
  phone: {
    label: "Phone number",
    hint: "912 345 678",
    keyboard: "numeric",
    maxLength: 16,
    allowed: /[0-9+ ]/,
    validate: (v) => (v.replace(/\D/g, "").length >= 9 ? null : "Enter a valid phone number"),
  },
  city: { label: "City", hint: "Your city", keyboard: "text", maxLength: 40 },
  address: { label: "Full address", hint: "Street, number, floor", keyboard: "text", maxLength: 100 },
  postalCode: {
    label: "Postal code",
    hint: "0000-000",
    keyboard: "numeric",
    maxLength: 8,
    allowed: /[0-9-]/,
    validate: (v) => (/^\d{4}-\d{3}$/.test(v) ? null : "Use the format 0000-000"),
  },
  nif: {
    label: "NIF",
    hint: "9 digits",
    keyboard: "numeric",
    maxLength: 9,
    allowed: /[0-9]/,
    validate: (v) => (/^\d{9}$/.test(v) ? null : "NIF must have 9 digits"),
  },
};

// Same two options and fields as https://evomedia.pt/formulario.html
const PLANS: { id: RegistrationPlan; label: string }[] = [
  { id: "fast", label: "FAST TV" },
  { id: "ppv", label: "PPV" },
];
const PLAN_FIELDS: Record<RegistrationPlan, FieldId[]> = {
  fast: ["name", "phone", "city"],
  ppv: ["name", "phone", "city", "address", "postalCode", "nif"],
};

// Layout, in canvas pixels. Sized so the PPV form (6 fields + consent) still
// fits between the logo and the bottom of the screen.
const PANEL_Y = 360;
const PANEL_H = 700;
const LEFT_X = 140;
const LEFT_W = 640;
const RIGHT_X = 820;
const RIGHT_W = 960;
const PAD = 30;
const ROW_W = LEFT_W - PAD * 2;
const ROW_H = 58;
const CONSENT_H = 44;
const FIELD_Y0 = 108;
const FIELD_PITCH = 68;

const C = {
  panelTop: "#000000B3",
  panelBottom: "#000000F2",
  row: "#1C1C20",
  rowFocused: "#2C2C33",
  rowBorder: "#3A3A42",
  muted: "#B9B9BE",
  error: "#FF8A80",
  white: "#FFFFFF",
};

type TvRegistrationFormProps = {
  deviceId: string;
  accent: string;
  // True once a registration went through. Kept by the parent so it
  // survives this form being unmounted while "Check activation" runs.
  sent: boolean;
  onSubmit: (req: RegistrationRequest) => Promise<void>;
  onCheckActivation: () => void;
  // Up from the top row, or Back: hand focus back to the parent's tabs
  onUp: () => void;
  formRef?: { focusEntry?: () => void };
};

const TvRegistrationForm = (props: TvRegistrationFormProps) => {
  const [plan, setPlan] = createSignal<RegistrationPlan>("fast");
  const [values, setValues] = createStore<Record<FieldId, string>>({
    name: "", phone: "", city: "", address: "", postalCode: "", nif: "",
  });
  // Which element has focus, e.g. "plan-fast", "field-name", "submit"
  const [focused, setFocused] = createSignal<string | null>(null);
  // The field the on-screen keyboard is typing into
  const [editing, setEditing] = createSignal<FieldId | null>(null);
  const [errors, setErrors] = createSignal<Partial<Record<FieldId, string>>>({});
  const [formError, setFormError] = createSignal("");
  const [submitting, setSubmitting] = createSignal(false);
  // The API needs the customer's consent, so it is an explicit checkbox
  const [consent, setConsent] = createSignal(false);

  const planRefs: Partial<Record<RegistrationPlan, any>> = {};
  const fieldRefs: Partial<Record<FieldId, any>> = {};
  let submitRef: any;
  let consentRef: any;
  let checkRef: any;
  const keyboardHandle: { focus?: () => void } = {};

  const fields = () => PLAN_FIELDS[plan()];
  const focusedField = () => {
    const f = focused();
    return f?.startsWith("field-") ? (f.slice(6) as FieldId) : null;
  };
  // Field shown in the keyboard panel: the one being typed into, otherwise
  // the highlighted row, so the panel previews what OK will edit
  const previewField = () => editing() ?? focusedField();
  const consentY = () => FIELD_Y0 + fields().length * FIELD_PITCH + 4;
  const submitY = () => consentY() + CONSENT_H + 12;

  const focusProps = (id: string) => ({
    onFocus: () => setFocused(id),
    onBlur: () => setFocused((f) => (f === id ? null : f)),
  });

  const clearError = (id: FieldId) => {
    if (errors()[id]) setErrors((e) => ({ ...e, [id]: undefined }));
    setFormError("");
  };

  const addChar = (id: FieldId, c: string) => {
    const def = FIELDS[id];
    if (def.allowed && !def.allowed.test(c)) return;
    setValues(id, (v) => {
      // Postal code: insert the dash after the first 4 digits automatically
      if (id === "postalCode" && v.length === 4 && /\d/.test(c) && !v.includes("-")) v += "-";
      return (v + c).slice(0, def.maxLength);
    });
    clearError(id);
  };

  const deleteChar = (id: FieldId) => {
    setValues(id, (v) => v.slice(0, -1));
    clearError(id);
  };

  const startEditing = (id: FieldId) => {
    setEditing(id);
    // The keyboard is rebuilt when its layout changes (text or numeric), so
    // focus it after this update has rendered
    setTimeout(() => keyboardHandle.focus?.(), 0);
  };

  const stopEditing = () => {
    const id = editing();
    setEditing(null);
    if (id) fieldRefs[id]?.setFocus();
  };

  // Keyboard "OK": continue with the next field, or go to the consent box after the last one
  const nextField = () => {
    const list = fields();
    const idx = list.indexOf(editing()!);
    if (idx >= 0 && idx < list.length - 1) {
      startEditing(list[idx + 1]);
    } else {
      setEditing(null);
      consentRef?.setFocus();
    }
  };

  const selectPlan = (p: RegistrationPlan) => {
    setPlan(p);
    setErrors({});
    setFormError("");
  };

  const validate = () => {
    const found: Partial<Record<FieldId, string>> = {};
    for (const id of fields()) {
      const v = values[id].trim();
      const err = v ? FIELDS[id].validate?.(v) : "Required";
      if (err) found[id] = err;
    }
    setErrors(found);
    return fields().find((id) => found[id]) ?? null;
  };

  const submit = async () => {
    if (submitting()) return;
    const firstInvalid = validate();
    if (firstInvalid) {
      setFormError("Please check the highlighted fields.");
      fieldRefs[firstInvalid]?.setFocus();
      return;
    }
    if (!consent()) {
      setFormError("Please tick the consent box to continue.");
      consentRef?.setFocus();
      return;
    }
    const data = Object.fromEntries(fields().map((id) => [id, values[id].trim()]));
    setSubmitting(true);
    setFormError("");
    try {
      await props.onSubmit({ plan: plan(), deviceId: props.deviceId, consent: consent(), ...data } as RegistrationRequest);
    } catch (e: any) {
      setFormError(e?.message || "Could not send the registration. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // The Send button is replaced by "Check activation" once sent: move focus there
  createEffect(on(() => props.sent, (sent) => {
    if (sent) setTimeout(() => checkRef?.setFocus(), 0);
  }));

  onMount(() => {
    if (props.formRef) {
      props.formRef.focusEntry = () =>
        props.sent ? checkRef?.setFocus() : planRefs[plan()]?.setFocus();
    }

    // Typing on a hardware keyboard, or the remote's number keys, goes into
    // the field being edited, or into the highlighted row
    const onKey = (e: KeyboardEvent) => {
      const id = editing() ?? focusedField();
      if (!id || props.sent) return;
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) addChar(id, e.key);
    };
    document.addEventListener("keydown", onKey);
    onCleanup(() => document.removeEventListener("keydown", onKey));
  });

  const toTabs = () => { props.onUp(); return true; };

  return (
    <Show
      when={!props.sent}
      fallback={
        // ── Sent ──
        <View x={LEFT_X} y={PANEL_Y} width={LEFT_W} height={440} borderRadius={30}
          style={{ colorTop: hexColor(C.panelTop), colorBottom: hexColor(C.panelBottom) }}>
          <View x={50} y={50} style={{ display: "flex", flexDirection: "column", gap: 24, width: 540 }}>
            <Text style={{ fontSize: 36, fontWeight: "bold", color: hexColor("#00C46A") }}>
              Registration sent
            </Text>
            <Text contain="width" style={{ fontSize: 22, lineHeight: 30, width: 540, maxLines: 3, color: hexColor(C.muted) }}>
              Thank you! Your device will be activated shortly. Once it is, press Check activation to start watching.
            </Text>
            <Box width={540} height={80} radius={40} fill={focused() === "check" ? C.white : props.accent}
              border={focused() === "check" ? C.white : props.accent}>
              <View ref={(el: any) => { checkRef = el; }} width={540} height={80}
                {...focusProps("check")}
                onEnter={() => { props.onCheckActivation(); return true; }}
                onUp={toTabs}
                onEscape={toTabs}>
                <Text y={26} contain="width" textAlign="center"
                  style={{ width: 540, fontSize: 26, fontWeight: "bold" }} color={hexColor(focused() === "check" ? "#000000" : C.white)}>
                  Check activation
                </Text>
              </View>
            </Box>
          </View>
        </View>
      }
    >
      {/* ── Left panel: plan + fields + send ── */}
      <View x={LEFT_X} y={PANEL_Y} width={LEFT_W} height={PANEL_H} borderRadius={30}
        style={{ colorTop: hexColor(C.panelTop), colorBottom: hexColor(C.panelBottom) }}>

        <For each={PLANS}>
          {(p, i) => {
            const id = `plan-${p.id}`;
            const selected = () => plan() === p.id;
            const isFocused = () => focused() === id;
            const w = (ROW_W - 20) / 2;
            return (
              <Box x={PAD + i() * (w + 20)} y={28} width={w} height={ROW_H} radius={30}
                borderWidth={3}
                fill={selected() ? props.accent : C.row}
                border={isFocused() ? C.white : selected() ? props.accent : C.rowBorder}>
                <View ref={(el: any) => { planRefs[p.id] = el; }} width={w} height={ROW_H}
                  {...focusProps(id)}
                  onEnter={() => { selectPlan(p.id); return true; }}
                  onLeft={() => { if (i() > 0) planRefs[PLANS[i() - 1].id]?.setFocus(); return true; }}
                  onRight={() => { planRefs[PLANS[i() + 1]?.id]?.setFocus(); return true; }}
                  onUp={toTabs}
                  onDown={() => { fieldRefs[fields()[0]]?.setFocus(); return true; }}
                  onEscape={toTabs}>
                  <Text y={17} contain="width" textAlign="center"
                    style={{ width: w, fontSize: 24, fontWeight: "bold", color: hexColor(C.white) }}>
                    {p.label}
                  </Text>
                </View>
              </Box>
            );
          }}
        </For>

        <For each={fields()}>
          {(id, i) => {
            const def = FIELDS[id];
            const isFocused = () => focused() === `field-${id}`;
            const isEditing = () => editing() === id;
            const error = () => errors()[id];
            return (
              <Box x={PAD} y={FIELD_Y0 + i() * FIELD_PITCH} width={ROW_W} height={ROW_H} radius={14}
                fill={isFocused() || isEditing() ? C.rowFocused : C.row}
                border={isEditing() ? props.accent : error() ? C.error : isFocused() ? C.white : C.rowBorder}>
                <View ref={(el: any) => { fieldRefs[id] = el; }} width={ROW_W} height={ROW_H}
                  {...focusProps(`field-${id}`)}
                  onEnter={() => { startEditing(id); return true; }}
                  onRight={() => { startEditing(id); return true; }}
                  onLeft={() => true}
                  onUp={() => {
                    const prev = fields()[i() - 1];
                    if (prev) fieldRefs[prev]?.setFocus();
                    else planRefs[plan()]?.setFocus();
                    return true;
                  }}
                  onDown={() => {
                    const next = fields()[i() + 1];
                    if (next) fieldRefs[next]?.setFocus();
                    else consentRef?.setFocus();
                    return true;
                  }}
                  onBackspace={() => { deleteChar(id); return true; }}
                  onEscape={toTabs}>
                  <Text x={22} y={7} style={{ fontSize: 15 }} color={hexColor(error() ? C.error : C.muted)}>
                    {error() ? `${def.label} - ${error()}` : def.label}
                  </Text>
                  <Text x={22} y={26} contain="width"
                    style={{ width: ROW_W - 44, maxLines: 1, fontSize: 24 }} color={hexColor(values[id] ? C.white : "#6E6E76")}>
                    {values[id] || def.hint}
                  </Text>
                </View>
              </Box>
            );
          }}
        </For>

        {/* Consent: OK toggles it. Sending is blocked until it's ticked. */}
        <View ref={(el: any) => { consentRef = el; }} x={PAD} y={consentY()} width={ROW_W} height={CONSENT_H}
          {...focusProps("consent")}
          onEnter={() => { setConsent((c) => !c); setFormError(""); return true; }}
          onUp={() => { fieldRefs[fields()[fields().length - 1]]?.setFocus(); return true; }}
          onDown={() => { submitRef?.setFocus(); return true; }}
          onLeft={() => true}
          onRight={() => true}
          onEscape={toTabs}>
          <Box y={4} width={36} height={36} radius={8} borderWidth={3}
            fill={consent() ? props.accent : C.row}
            border={focused() === "consent" ? C.white : consent() ? props.accent : C.rowBorder}>
            <Show when={consent()}>
              <View x={11} y={11} width={14} height={14} borderRadius={3} color={hexColor(C.white)} />
            </Show>
          </Box>
          <Text x={52} y={11} contain="width" style={{ width: ROW_W - 52, maxLines: 1, fontSize: 19 }}
            color={hexColor(focused() === "consent" ? C.white : C.muted)}>
            I agree to share these details with Evomedia
          </Text>
        </View>

        <Box x={PAD} y={submitY()} width={ROW_W} height={ROW_H} radius={30}
          fill={focused() === "submit" ? C.white : props.accent}
          border={focused() === "submit" ? C.white : props.accent}>
          <View ref={(el: any) => { submitRef = el; }} width={ROW_W} height={ROW_H}
            {...focusProps("submit")}
            onEnter={() => { submit(); return true; }}
            onUp={() => { consentRef?.setFocus(); return true; }}
            onDown={() => true}
            onLeft={() => true}
            onRight={() => true}
            onEscape={toTabs}>
            <Text y={17} contain="width" textAlign="center"
              style={{ width: ROW_W, fontSize: 24, fontWeight: "bold" }} color={hexColor(focused() === "submit" ? "#000000" : C.white)}>
              {submitting() ? "Sending..." : "Send registration"}
            </Text>
          </View>
        </Box>

        <Show when={formError()}>
          <Text x={PAD} y={submitY() + ROW_H + 16} contain="width"
            style={{ width: ROW_W, maxLines: 2, fontSize: 20, color: hexColor(C.error) }}>
            {formError()}
          </Text>
        </Show>
      </View>

      {/* ── Right panel: what is being typed + keyboard ── */}
      <View x={RIGHT_X} y={PANEL_Y} width={RIGHT_W} height={PANEL_H} borderRadius={30}
        style={{ colorTop: hexColor(C.panelTop), colorBottom: hexColor(C.panelBottom) }}>
        <Text x={50} y={34} style={{ fontSize: 20, color: hexColor(C.muted) }}>
          {previewField() ? FIELDS[previewField()!].label : "Select a field to start typing"}
        </Text>

        <Box x={50} y={66} width={RIGHT_W - 100} height={76} radius={14} fill={C.row}
          border={editing() ? props.accent : C.rowBorder}>
          <Show when={previewField()}>
            {(id) => (
              <Text x={24} y={20} contain="width"
                style={{ width: RIGHT_W - 148, maxLines: 1, fontSize: 32 }} color={hexColor(values[id()] ? C.white : "#6E6E76")}>
                {values[id()] ? values[id()] + (editing() ? "|" : "") : FIELDS[id()].hint}
              </Text>
            )}
          </Show>
        </Box>

        {/* Rebuilt when the layout changes; dimmed until a field is being edited */}
        <Show when={previewField() ? FIELDS[previewField()!].keyboard : "text"} keyed>
          {(mode: KeyboardMode) => (
            <View x={(RIGHT_W - keyboardWidth(mode)) / 2} y={170} alpha={editing() ? 1 : 0.4}>
              <OnScreenKeyboard
                mode={mode}
                accent={props.accent}
                keyboardRef={keyboardHandle}
                onChar={(c) => { const id = editing(); if (id) addChar(id, c); }}
                onDelete={() => { const id = editing(); if (id) deleteChar(id); }}
                onDone={nextField}
                onExit={stopEditing}
              />
            </View>
          )}
        </Show>

        <Text x={50} y={PANEL_H - 70} contain="width" textAlign="center"
          style={{ width: RIGHT_W - 100, fontSize: 18, color: hexColor(C.muted) }}>
          {editing()
            ? "OK: next field   |   Back: stop typing"
            : "Press OK on a field to type. You can also use the number keys on your remote."}
        </Text>
      </View>
    </Show>
  );
};

export default TvRegistrationForm;
